import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-send.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-attach.js';

type RemoteAppMessage = {
  role: 'user' | 'assistant';
  content: string | (() => string);
  imagePlaceholder?: boolean;
  imageUrl?: string;
  attachmentsCount?: number;
};

@customElement('kv-module-remoteapp')
@localized()
export class KvModuleRemoteApp extends LitElement {
  static styles = css`
    :host {
      display: flex;
      height: 100vh;
      width: 100%;
    }
    .main {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-editor-bg);
    }
    .chat-panel {
      width: 380px;
      background-color: var(--color-sidebar-bg);
      color: var(--color-chat-text);
      display: flex;
      flex-direction: column;
      *, *::after, *::before {
        user-select: text;
        -webkit-user-select: text;
        cursor: auto;
      }
    }
    .messages {
      flex: 1;
      padding: var(--gap-4);
      overflow-y: auto;
      display: flex;
      flex-direction: column;
    }
    .message {
      max-width: 900px;
      margin: 0 0 var(--gap-3);
      padding: var(--gap-1);
      border-radius: 20px;
      line-height: 1.5;
      display: inline-block;
      align-self: flex-start;
    }
    .message .content {
      white-space: pre-wrap;
    }
    .message .image-ph {
      margin-top: var(--gap-2);
      width: 100%;
      height: 160px;
      border: 2px dashed var(--color-chat-placeholder-border);
      border-radius: 12px;
      background: var(--color-chat-placeholder-bg);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-chat-placeholder-text);
      font-size: 14px;
    }
    .message .attachments {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-top: var(--gap-2);
      padding: 6px 10px;
      border-radius: 999px;
      border: var(--border);
      background: var(--color-chat-attachments-bg);
      font-size: 12px;
      color: var(--color-chat-attachments-text);
    }
    .message .attachments sp-icon-attach {
      width: 16px;
      height: 16px;
      color: var(--color-chat-attachments-icon);
    }
    .message img.image-attachment {
      display: block;
      margin-top: var(--gap-2);
      max-width: 400px;
      width: 100%;
      height: auto;
      border-radius: 12px;
      border: var(--border);
      background: var(--color-chat-image-bg);
    }
    .assistant {
      background: var(--color-chat-assistant-bg);
    }
    .user {
      background: var(--color-chat-user-bg);
      text-align: right;
      align-self: flex-end;
      padding: var(--gap-1) var(--gap-2);
    }
    .input {
      display: flex;
      gap: var(--gap-2);
      padding: var(--gap-2);
    }
    .input textarea {
      flex: 1;
      resize: none;
      min-height: 44px;
      max-height: 160px;
      padding: 10px 12px;
      border-radius: 12px;
      border: 1px solid var(--color-chat-input-border);
      background: var(--color-chat-input-bg);
      color: inherit;
      font: inherit;
      outline: none;
    }
  `;

  @state()
  accessor messages: RemoteAppMessage[] = [
    {
      role: 'user',
      content: () => msg('Send this client a mail about the missing receipts.'),
    },
    {
      role: 'assistant',
      content: () =>
        msg(`Sure, here is the mail to client1005@example.com:

Dear Mr. Cheese,

we noticed two missing receipts from March 19, 2024, for €155 and €123. Could you please send them to us by mail?

Thank you!

Best,
Peter Parker

Want me to send it?`),
    },
    {
      role: 'user',
      content: () => msg('Yes, please'),
    },
    {
      role: 'assistant',
      content: () => msg('Done'),
    },
  ];

  @state()
  accessor inputText: string = '';

  private onSend() {
    const text = this.inputText.trim();
    if (!text) return;
    this.messages = [...this.messages, { role: 'user', content: text }];
    this.messages = [
      ...this.messages,
      {
        role: 'assistant',
        content: () =>
          msg(
            'Acknowledged. This is a mock reply from Remote App. We will execute tasks here.'
          ),
      },
    ];
    this.inputText = '';
    requestAnimationFrame(() => {
      const scroller = this.renderRoot?.querySelector('.messages');
      if (scroller) (scroller as HTMLElement).scrollTop = scroller.scrollHeight;
      const ta = this.renderRoot?.querySelector(
        'textarea'
      ) as HTMLTextAreaElement | null;
      if (ta) {
        ta.style.height = 'auto';
        ta.focus();
      }
    });
  }

  private onInput(e: InputEvent) {
    const el = e.target as HTMLTextAreaElement;
    this.inputText = el.value;
    el.style.height = 'auto';
    const max = 160;
    const next = Math.min(max, el.scrollHeight);
    el.style.height = `${next}px`;
  }

  private onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.onSend();
    }
  }

  private resolveMessageContent(message: RemoteAppMessage): string {
    return typeof message.content === 'function'
      ? message.content()
      : message.content;
  }

  render() {
    return html`
      <div class="chat-panel">
        <div class="messages">
          ${this.messages.map((m) => {
            const content = this.resolveMessageContent(m);
            return html`<div class="message ${m.role}">
              <div class="content">${m.role === 'user' ? html`<shrink-pill max-width="250" max-inline-size="30">${content}</shrink-pill>` : content}</div>
              ${
                m.attachmentsCount && m.attachmentsCount > 0
                  ? html`<div class="attachments">
                    <sp-icon-attach></sp-icon-attach>
                    <span>${msg(str`${m.attachmentsCount} files attached`)}</span>
                  </div>`
                  : null
              }
              ${
                m.imageUrl
                  ? html`<img
                    class="image-attachment"
                    src="${m.imageUrl}"
                    alt=${msg('Attachment')}
                    loading="lazy"
                  />`
                  : m.imagePlaceholder
                    ? html`<div class="image-ph">${msg('Image placeholder')}</div>`
                    : null
              }
            </div>`;
          })}
        </div>
        <div class="input">
          <textarea
            placeholder=${msg('Ask anything')}
            .value=${this.inputText}
            @input=${(e: InputEvent) => this.onInput(e)}
            @keydown=${(e: KeyboardEvent) => this.onKeydown(e)}
          ></textarea>
          <sp-action-button
            quiet
            title=${msg('Send')}
            ?disabled=${!this.inputText.trim()}
            @click=${this.onSend}
          >
            <sp-icon-send slot="icon"></sp-icon-send>
          </sp-action-button>
        </div>
      </div>
      <div class="main">
        <img
          src="/mock/book-receipts.png"
          alt=${msg('Book receipts preview')}
          style="max-width:100%;height:auto;"
        />
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-remoteapp': KvModuleRemoteApp;
  }
}
